import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { FiMail, FiLock, FiLogIn, FiEye, FiEyeOff } from 'react-icons/fi';

// Just A Smile Events Logo Component - Taille doublée
const JustASmileLogo = ({ className = "" }) => (
  <div className={`flex flex-col items-center ${className}`}>
    <img 
      src="https://2026.justasmile-events.com/wp-content/uploads/2023/04/JUST-A-SMILE-EVENTS-LOGO-new-2025-sans-fond-1-1024x1024.png"
      alt="Just A Smile Events"
      className="h-40 w-auto mb-2"
    />
  </div>
);

// Background Video ID (même que la page d'accueil)
const HERO_VIDEO_ID = '_DcxsNgZTx8';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const result = await login(email, password);
    
    if (result.success) {
      // Get user role from localStorage to determine redirect
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const userData = JSON.parse(userStr);
        if (userData.role === 'client') {
          navigate('/mon-compte');
        } else {
          navigate('/dashboard');
        }
      } else {
        navigate('/dashboard');
      }
    } else {
      setError(result.error);
    }
    
    setLoading(false);
  };

  return (
    <div className="min-h-screen relative flex items-center justify-center p-4 overflow-hidden">
      {/* Video Background */}
      <div className="absolute inset-0 w-full h-full">
        <iframe
          src={`https://www.youtube.com/embed/${HERO_VIDEO_ID}?autoplay=1&mute=1&loop=1&playlist=${HERO_VIDEO_ID}&controls=0&showinfo=0&rel=0&modestbranding=1&playsinline=1&enablejsapi=1`}
          title="Background Video"
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none scale-150"
          style={{ 
            border: 'none',
            width: '100vw',
            height: '100vh',
            minWidth: '177.78vh',
            minHeight: '100vh'
          }}
          allow="autoplay; encrypted-media"
          allowFullScreen
        />
      </div>
      
      {/* Overlay noir 70% */}
      <div className="absolute inset-0 bg-black/70 z-10" />
      
      {/* Content */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md relative z-20"
      >
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex justify-center">
            <JustASmileLogo />
          </Link>
          
          {/* Message de bienvenue personnalisé */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.5 }}
            className="mt-6"
          >
            <h2 className="text-2xl font-light text-white mb-2">
              Bonjour <span className="font-semibold text-[#d4af37]">Mounir</span>,
            </h2>
            <p className="text-lg text-white/80">
              bienvenue dans votre espace !
            </p>
          </motion.div>
        </div>

        <Card className="bg-slate-800/90 backdrop-blur-sm border-slate-700/50 shadow-2xl">
          <CardHeader>
            <CardTitle className="text-2xl text-center text-white">Connexion</CardTitle>
            <CardDescription className="text-center text-slate-400">
              Accédez à votre tableau de bord
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="bg-red-500/10 border border-red-500/20 text-red-400 px-4 py-3 rounded-lg text-sm" data-testid="login-error">
                  {error}
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="email" className="text-slate-300">Email</Label>
                <div className="relative">
                  <FiMail className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="votre@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 bg-slate-700/80 border-slate-600 text-white placeholder:text-slate-500 focus:border-[#d4af37]"
                    required
                    data-testid="login-email"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-slate-300">Mot de passe</Label>
                <div className="relative">
                  <FiLock className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 pr-10 bg-slate-700/80 border-slate-600 text-white placeholder:text-slate-500 focus:border-[#d4af37]"
                    required
                    data-testid="login-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 transition-colors"
                    data-testid="toggle-password-visibility"
                  >
                    {showPassword ? <FiEyeOff className="h-5 w-5" /> : <FiEye className="h-5 w-5" />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                className="w-full bg-[#d4af37] hover:bg-[#b8972e] text-black font-semibold rounded-lg py-6"
                disabled={loading}
                data-testid="login-submit"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <span className="animate-spin rounded-full h-4 w-4 border-2 border-black border-t-transparent" />
                    Connexion...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <FiLogIn />
                    Se connecter
                  </span>
                )}
              </Button>

            </form>
          </CardContent>
        </Card>
        
        {/* Footer discret */}
        <p className="text-center text-white/40 text-xs mt-6">
          Just A Smile Events © 2026
        </p>
      </motion.div>
    </div>
  );
}
